import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Link } from 'react-router-dom';
import { Topic, UserProgress } from '../types';
import {
  BookOpen,
  Lock,
  Play,
  Filter,
} from 'lucide-react';

export default function Topics() {
  const { user } = useAuth();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [progress, setProgress] = useState<Map<number, UserProgress>>(
    new Map()
  );
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(user?.current_year || 1);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    fetchTopics();
  }, [selectedYear]);

  const fetchTopics = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [topicsRes, progressRes] = await Promise.all([
        fetch(`/api/topics?year=${selectedYear}`, { headers }),
        fetch('/api/progress', { headers }),
      ]);

      if (topicsRes.ok) {
        const data = await topicsRes.json();
        setTopics(data.topics);
      }

      if (progressRes.ok) {
        const data = await progressRes.json();
        const progressMap = new Map<number, UserProgress>(
          data.progress.map((p: UserProgress) => [p.topic_id, p])
        );
        setProgress(progressMap);
      }
    } catch (error) {
      console.error('Failed to fetch topics:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'all', label: 'Alle Kategorien' },
    { value: 'allgemein', label: 'Allgemein' },
    { value: 'hardware', label: 'Hardware' },
    { value: 'netzwerk', label: 'Netzwerk' },
    { value: 'server', label: 'Server' },
    { value: 'security', label: 'Security' },
    { value: 'projektmanagement', label: 'Projektmanagement' },
    { value: 'kunde', label: 'Kunde' },
    { value: 'schwerpunkt_systemtechnik', label: 'Schwerpunkt Systemtechnik' },
  ];

  const filteredTopics = topics.filter(
    (topic) =>
      selectedCategory === 'all' || topic.category === selectedCategory
  );

  const getMasteryLevel = (topicId: number) => {
    return progress.get(topicId)?.mastery_level || 0;
  };

  const getMasteryColor = (level: number) => {
    if (level >= 80) return 'bg-green-500';
    if (level >= 60) return 'bg-yellow-500';
    if (level >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const isTopicUnlocked = (topic: Topic) => {
    return topic.year_level <= (user?.current_year || 1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">Lernthemen</h1>
        <p className="mt-2 text-gray-600">
          Wähle ein Thema aus und starte mit dem Lernen
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Year Filter */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lehrjahr
            </label>
            <div className="flex space-x-2">
              {[1, 2, 3, 4].map((year) => (
                <button
                  key={year}
                  onClick={() => setSelectedYear(year)}
                  disabled={year > (user?.current_year || 1)}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedYear === year
                      ? 'bg-primary-600 text-white'
                      : year > (user?.current_year || 1)
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {year}. LJ
                  {year > (user?.current_year || 1) && (
                    <Lock className="inline-block ml-1 h-3 w-3" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Filter className="inline-block h-4 w-4 mr-1" />
              Kategorie
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Topics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTopics.map((topic) => {
          const masteryLevel = getMasteryLevel(topic.id);
          const unlocked = isTopicUnlocked(topic);

          return (
            <div
              key={topic.id}
              className={`bg-white rounded-lg shadow overflow-hidden ${
                !unlocked ? 'opacity-60' : ''
              }`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <BookOpen className="h-5 w-5 text-primary-600 mr-2" />
                      <span className="text-xs font-medium text-gray-500">
                        {topic.code}
                      </span>
                    </div>
                    <h3 className="mt-2 text-lg font-semibold text-gray-900">
                      {topic.title}
                    </h3>
                    <p className="mt-2 text-sm text-gray-600 line-clamp-3">
                      {topic.description}
                    </p>
                  </div>
                  {!unlocked && (
                    <Lock className="h-5 w-5 text-gray-400 ml-2" />
                  )}
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                    <span>Beherrschung</span>
                    <span className="font-medium">{masteryLevel}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${getMasteryColor(
                        masteryLevel
                      )}`}
                      style={{ width: `${masteryLevel}%` }}
                    />
                  </div>
                </div>

                <div className="mt-4 text-xs text-gray-500">
                  <p>Paragraph: {topic.paragraph_reference}</p>
                  <p className="mt-1">
                    Kategorie:{' '}
                    {
                      categories.find((c) => c.value === topic.category)
                        ?.label
                    }
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4">
                {unlocked ? (
                  <Link
                    to={`/test/${topic.id}`}
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Test starten
                  </Link>
                ) : (
                  <button
                    disabled
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-400 bg-gray-100 cursor-not-allowed"
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    Gesperrt
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredTopics.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            Keine Themen gefunden
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Wähle ein anderes Lehrjahr oder eine andere Kategorie
          </p>
        </div>
      )}
    </div>
  );
}
